import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '@/contexts/DataContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoIcon, ArrowLeft, ArrowRight, PlusCircle, X, Maximize2, Minimize2 } from 'lucide-react';

export default function PitchCreateCompetition() {
  const { saveTempPitch, getTempPitch } = useData();
  const navigate = useNavigate();
  const [fullscreen, setFullscreen] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState('');
  const [newBarrier, setNewBarrier] = useState('');
  
  const [form, setForm] = useState(() => {
    const saved = getTempPitch();
    return {
      competitive: saved?.competitive || {
        directCompetitors: [],
        indirectCompetitors: [],
        competitiveAdvantage: '',
        barriersToEntry: [],
        substituteProducts: [],
      }
    };
  });

  useEffect(() => {
    saveTempPitch(form);
  }, [form, saveTempPitch]);

  const handleBack = () => {
    navigate('/pitch/new/traction');
  };

  const handleNext = () => {
    navigate('/pitch/new/financial');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setFullscreen(false);
      }
    }
  };

  const addArrayItem = (field: string, value: string) => {
    if (!value.trim()) return;
    setForm(prev => ({
      ...prev,
      competitive: {
        ...prev.competitive,
        [field]: [...prev.competitive[field as keyof typeof prev.competitive] as string[], value.trim()]
      }
    }));
  };

  const removeArrayItem = (field: string, index: number) => {
    setForm(prev => ({
      ...prev,
      competitive: {
        ...prev.competitive,
        [field]: (prev.competitive[field as keyof typeof prev.competitive] as string[]).filter((_, i) => i !== index)
      }
    }));
  };

  return (
    <div className={`${fullscreen ? 'fixed inset-0 z-50 bg-background overflow-auto p-8' : ''}`}>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Competitive Landscape</h1>
            <p className="text-muted-foreground mt-1">Step 3 of 8 · How do you stack up against the competition?</p>
          </div>
          <Button variant="outline" size="icon" onClick={toggleFullscreen}>
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Market Competition</CardTitle>
            <CardDescription>
              Investors want to know you understand your competitive landscape.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <Alert className="bg-purple-50 border-purple-200">
              <InfoIcon className="h-4 w-4 text-purple-600" />
              <AlertDescription className="text-purple-700">
                "If you have no competitors, you probably don't have a market." Be honest about who else is solving this problem.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <Label className="text-lg font-medium">Direct Competitors</Label>
              <div className="flex gap-2">
                <Input
                  value={newCompetitor}
                  onChange={e => setNewCompetitor(e.target.value)}
                  placeholder="Add a direct competitor (e.g., Uber, Lyft)"
                  className="text-base py-6"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addArrayItem('directCompetitors', newCompetitor);
                      setNewCompetitor('');
                    }
                  }}
                />
                <Button 
                  type="button" 
                  onClick={() => {
                    addArrayItem('directCompetitors', newCompetitor);
                    setNewCompetitor('');
                  }}
                  className="px-6"
                >
                  <PlusCircle className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {form.competitive.directCompetitors.map((comp, i) => (
                  <div key={i} className="flex items-center gap-1 bg-secondary px-3 py-2 rounded-md">
                    <span className="text-sm">{comp}</span>
                    <X 
                      className="h-4 w-4 cursor-pointer hover:text-red-500" 
                      onClick={() => removeArrayItem('directCompetitors', i)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-lg font-medium">Competitive Advantage</Label>
              <Textarea
                value={form.competitive.competitiveAdvantage}
                onChange={e => setForm({
                  ...form,
                  competitive: {
                    ...form.competitive,
                    competitiveAdvantage: e.target.value
                  }
                })}
                placeholder="What makes you different and defensible? Technology? Network effects? Brand? Data? First-mover advantage?"
                className="min-h-[150px] text-base p-4"
              />
              <p className="text-sm text-muted-foreground italic">
                💡 Be specific. "We have a patent" vs "We're better" — which is more convincing?
              </p>
            </div>

            <div className="space-y-3">
              <Label className="text-lg font-medium">Barriers to Entry</Label>
              <div className="flex gap-2">
                <Input
                  value={newBarrier}
                  onChange={e => setNewBarrier(e.target.value)}
                  placeholder="Add a barrier (e.g., High capital requirements, Regulatory approvals)"
                  className="text-base py-6"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addArrayItem('barriersToEntry', newBarrier);
                      setNewBarrier('');
                    }
                  }}
                />
                <Button 
                  type="button" 
                  onClick={() => {
                    addArrayItem('barriersToEntry', newBarrier);
                    setNewBarrier('');
                  }}
                  className="px-6"
                >
                  <PlusCircle className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {form.competitive.barriersToEntry.map((barrier, i) => (
                  <div key={i} className="flex items-center gap-1 bg-secondary px-3 py-2 rounded-md">
                    <span className="text-sm">{barrier}</span>
                    <X 
                      className="h-4 w-4 cursor-pointer hover:text-red-500" 
                      onClick={() => removeArrayItem('barriersToEntry', i)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between pt-6">
            <Button onClick={handleBack} variant="outline" size="lg" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back: Traction
            </Button>
            <Button onClick={handleNext} size="lg" className="gap-2">
              Next: Financial
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}